import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import injectReducer from '@utils/injectReducer';
import injectSaga from '@utils/injectSaga';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import Container from '@components/Container';
import Text, { TEXT_COLOR, TEXT_SIZE } from '@components/Text';
import CTAButton from '@components/CTAButton';
import TextButton from '@components/TextButton';
import CustomTextInput from '@components/CustomTextInput/index';
import { ROUTES } from '@shared/constants';
import {
  normalizeCognitoErrorMessage,
} from '@services/cognitoErrorNormalizer';
import { formHasErrors, getErrorMessageId } from '../validate';
import BackToSignInLink from '../shared/BackToSignInLink';
import {
  openSupportEmailPopupToSignIn,
} from '../shared/supportPopupHelpers';
import { openPopUp as openPopUpAction } from '../../PopUp/actions';
import POPUP_TYPES from '../../PopUp/types';
import {
  SUPPORT_EMAIL,
  toPlainErrorObject,
  getForgotPasswordServerErrorDisposition,
} from './helpers';
import { isConnectionErrorSentinel } from '@utils/errorUtils';
import {
  ForgotPasswordComponentContainer,
  WithHeaderComponentContainer,
  InputContainer,
  ButtonContainer,
  TextContainer,
  HelpMailLink,
  ForgotPasswordScreenContainer,
  HeaderContainer,
  StyledLogo,
} from './styles';
import LogoImage from '../logoTop.svg';
import { onSendPasswordLink } from './actions';
import reducer from './reducer';
import saga from './saga';
import {withTranslation} from "react-i18next";

// TODO: Add Help-center route

export class ForgotPassword extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      form: {
        values: {
          email: '',
        },
        errors: {
          email: null,
        },
      },
    };
  }

  componentDidMount() {
    const emailFromState = this.props?.history?.location?.state?.email;
    if (!emailFromState) return;

    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        values: {
          ...prevState.form.values,
          email: emailFromState,
        },
      },
    }));
  }

  componentDidUpdate(prevProps) {
    if (this.props.redirectToPage && this.props.redirectToPage !== prevProps.redirectToPage) {
      const { email } = this.state.form.values;

      const codeDeliveryDetails =
        this.props.codeDeliveryDetails && typeof this.props.codeDeliveryDetails.toJS === 'function'
          ? this.props.codeDeliveryDetails.toJS()
          : this.props.codeDeliveryDetails;

      this.props.history.push(this.props.redirectToPage, {
        email,
        codeDeliveryDetails: codeDeliveryDetails || null,
      });
    }

    if (
      this.props.serverError &&
      this.props.serverError !== prevProps.serverError
    ) {
      const err = toPlainErrorObject(this.props.serverError);
      const disposition = getForgotPasswordServerErrorDisposition(err);

      if (disposition.kind === 'VERIFY_EMAIL') {
        const { email } = this.state.form.values;
        this.props.history.push(ROUTES.VERIFY_EMAIL_SENT, {
          email,
          // After the user confirms their email, return them to Forgot Password.
          afterVerifiedRedirectTo: ROUTES.FORGOT_PASSWORD,
          afterVerifiedRedirectState: { email },
        });
        return;
      }

      if (disposition.kind === 'HELP_POPUP') {
        openSupportEmailPopupToSignIn({
          t: this.props.t,
          titleI18nKey: 'Errors.passwordResetUnavailableTitle',
          bodyI18nKey: 'Errors.passwordResetUnavailableBody',
          email: SUPPORT_EMAIL,
          history: this.props.history,
          openPopUp: this.props.openPopUp,
        });

        this.setState((prevState) => ({
          form: {
            ...prevState.form,
            errors: {
              ...prevState.form.errors,
              email: null,
            },
          },
          loading: false,
        }));
        return;
      }

      const message = isConnectionErrorSentinel(err)
        ? this.props.t('Errors.connectionErrorCheckInternet')
        : (normalizeCognitoErrorMessage(err, { defaultMessage: this.props.t('Errors.tryAgain') }) ||
          this.props.t('Errors.tryAgain'));

      this.setState((prevState) => ({
        form: {
          ...prevState.form,
          errors: {
            ...prevState.form.errors,
            email: message,
          },
        },
        loading: false,
      }));
    }
  }

  onFocus = (event) => {
    const name = event.target.name;
    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        errors: {
          ...prevState.form.errors,
          [name]: null,
        },
      },
    }));
  };

  onFieldUpdate = (event) => {
    const { value, name } = event.target;

    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        values: {
          ...prevState.form.values,
          [name]: value,
        },
      },
    }));
  };

  onSendButtonClick = () => {
    const { sendPasswordLink } = this.props;
    const { email } = this.state.form.values;

    const formErrors = this.getFormErrors();
    const loading = !formHasErrors(formErrors);

    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        errors: formErrors,
      },
      loading,
    }));

    if (loading) {
      sendPasswordLink(email);
    }
  };

  getFormErrors = () => {
    const { values } = this.state.form;
    const newErrors = {};

    _.forEach(values, (fieldValue, fieldName) => {
      const messageId = getErrorMessageId(fieldName, fieldValue);
      const fieldError = messageId
        ? this.props.t(`Errors.${messageId}`)
        : null;
      newErrors[fieldName] = fieldError;
    });

    return newErrors;
  };

  render() {
    const {t} = this.props
    const { values, errors } = this.state.form;
    return (
      <ForgotPasswordScreenContainer>
        <WithHeaderComponentContainer>
          <HeaderContainer>
            <StyledLogo src={LogoImage} alt='logo' />
            <Text textColor={TEXT_COLOR.DARK_BLUE} size={TEXT_SIZE.H1}>
              {t('Authentication.Password.reset')}
            </Text>
          </HeaderContainer>
          <ForgotPasswordComponentContainer id='forgotPassword'>
            <TextContainer>
              <Text textColor={TEXT_COLOR.DISABLED} size={TEXT_SIZE.H4}>
                {t('Authentication.Email.enterEmail')}
              </Text>
              <Text textColor={TEXT_COLOR.DISABLED} size={TEXT_SIZE.H4}>
                {t('Authentication.Password.receiveLink')}
              </Text>
            </TextContainer>

            <InputContainer>
              <CustomTextInput
                id='email_input'
                label={t('Authentication.Email.email')}
                name='email'
                error={errors.email}
                value={values.email}
                onChange={this.onFieldUpdate}
                onFocus={this.onFocus}
              />
            </InputContainer>

            <ButtonContainer>
              <CTAButton
                id='send_button'
                expanded
                onClick={this.onSendButtonClick}
              >
                {t('Common.Statuses.send')}
              </CTAButton>
            </ButtonContainer>

            <BackToSignInLink t={t} />

            <Container column>
              <Text textColor={TEXT_COLOR.DARK_BLUE} size={TEXT_SIZE.H4}>
                {t('Authentication.Distribution.needHelp')}
              </Text>
              <HelpMailLink id='help_center_link' href={`mailto:${SUPPORT_EMAIL}`}>
                <TextButton>
                  <Text size={TEXT_SIZE.H4}>
                    {SUPPORT_EMAIL}
                  </Text>
                </TextButton>
              </HelpMailLink>
            </Container>
          </ForgotPasswordComponentContainer>
        </WithHeaderComponentContainer>
      </ForgotPasswordScreenContainer>
    );
  }
}

ForgotPassword.propTypes = {
  sendPasswordLink: PropTypes.func.isRequired,
  openPopUp: PropTypes.func,
  history: PropTypes.object,
  codeDeliveryDetails: PropTypes.any,
};

export const mapStateToProps = (state) => {
  const serverError = state.getIn([
    'sendPasswordLink',
    'errors',
    'serverError',
  ]);
  const redirectToPage = state.getIn(['sendPasswordLink', 'redirectToPage']);
  const codeDeliveryDetails = state.getIn(['sendPasswordLink', 'codeDeliveryDetails']);
  return { serverError, redirectToPage, codeDeliveryDetails };
};

export const mapDispatchToProps = (dispatch) => ({
  sendPasswordLink: (email) => {
    dispatch(onSendPasswordLink(email));
  },
  openPopUp: (props) => dispatch(openPopUpAction(POPUP_TYPES.ALERT, props)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);
const withSaga = injectSaga({ key: 'sendPasswordLink', saga });
const withReducer = injectReducer({ key: 'sendPasswordLink', reducer });

export default compose(
  withTranslation(),
  withConnect,
  withRouter,
  withReducer,
  withSaga
)(ForgotPassword);
